/**
 * Пользовательские поля, у которых нет аналога среди штатных колонок (§4.4, §5.2):
 * «Пользователь» (комбобокс по семи пользователям, аватар 18 px в значении) и
 * «Мультивыбор» (поповер с чекбоксами, в ячейке — «2 значения» с тултипом).
 *
 * Остальные типы пользовательских полей рисуются теми же ячейками, что
 * штатные: text/textarea → TextCell, number → NumberCell, date → DateCell,
 * bool → BoolCell, url → UrlCell, select → EnumCell.
 */
import { useEffect, useRef, useState } from 'react'
import { Avatar, Checkbox, Popover, Select } from '../../../../components/index.js'
import { plural } from '../../../../texts.js'
import { tableTexts as t } from '../../texts.table.js'
import { userOptions } from '../values.js'
import type { CellProps } from './CellBox.js'

export function UserCell(props: CellProps) {
  const { column, cell, ctx, editing, readOnly, write, stepAfterCommit, commitAndRight, closeEditor } = props
  const wrap = useRef<HTMLDivElement>(null)
  const advancing = useRef(false)
  const direction = useRef<1 | -1>(1)

  useEffect(() => {
    if (!editing) return
    wrap.current?.querySelector<HTMLButtonElement>('button[role="combobox"]')?.focus()
  }, [editing])

  const id = cell.value === null || cell.value === undefined ? null : String(cell.value)
  const user = ctx.users.find((item) => String(item.id) === id)

  if (editing && !readOnly) {
    return (
      <div
        className="cell__editor"
        ref={wrap}
        onKeyDownCapture={(event) => {
          if (event.key === 'Enter') { direction.current = event.shiftKey ? -1 : 1; return }
          if (event.key === 'Tab') { event.preventDefault(); event.stopPropagation(); commitAndRight() }
        }}
      >
        <Select
          value={id}
          options={userOptions(ctx.users)}
          open
          onOpenChange={(open) => {
            if (open) return
            if (advancing.current) { advancing.current = false; return }
            closeEditor()
          }}
          onChange={(next) => {
            advancing.current = true
            write(next === null ? null : Number(next))
            stepAfterCommit(direction.current)
            direction.current = 1
          }}
          label={column.title}
          size="cell"
          variant="plain"
          emptyLabel={t.empty.dash}
        />
      </div>
    )
  }

  if (!user) return <span className="cell__empty">{t.empty.dash}</span>
  return (
    <span className="cell__value">
      <Avatar name={user.fullName} size={18} />
      {user.shortName}
    </span>
  )
}

const VALUE_FORMS = ['значение', 'значения', 'значений'] as const

export function MultiselectCell(props: CellProps) {
  const { column, cell, ctx, editing, readOnly, write, closeEditor } = props
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null)
  const selected = Array.isArray(cell.value) ? cell.value.map(String) : []
  const options = column.field?.config.options
    ?? (column.enumSet ? (ctx.enums[column.enumSet] ?? []).map((v) => ({ value: v.value, label: v.label })) : [])

  const labels = options.filter((option) => selected.includes(option.value)).map((option) => option.label)

  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value]
    write(next.length === 0 ? null : next)
  }

  const summary = selected.length === 0
    ? <span className="cell__empty">{t.empty.dash}</span>
    : selected.length === 1
      ? <span className="cell__value">{labels[0] ?? selected[0]}</span>
      : <span className="cell__value">{`${selected.length} ${plural(selected.length, VALUE_FORMS)}`}</span>

  if (!editing || readOnly) return summary

  return (
    <>
      <button
        ref={setAnchor}
        type="button"
        className="cell__value"
        aria-label={column.title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {labels.join(', ') || t.empty.dash}
      </button>
      <Popover open anchor={anchor} onClose={closeEditor} role="dialog" matchAnchorWidth minWidth={200}>
        <div className="cell__multi-list">
          {options.map((option) => (
            <div className="cell__multi-row" key={option.value}>
              <Checkbox
                checked={selected.includes(option.value)}
                onChange={() => toggle(option.value)}
                label={option.label}
              />
            </div>
          ))}
        </div>
      </Popover>
    </>
  )
}
